import { defineStore } from 'pinia'
import { computed, readonly, ref } from 'vue'

import { useSystemStore } from '@/client/store/useSystemStore'
import { type ColumnKey, TABLE_COLUMN_KEYS, type TableSettingsItem } from '@/typings/twitter_benchmark_campaign_posts'

import { getTwBenchmarkTableSettings } from '../api/getTwBenchmarkTableSettings'
import { putTwBenchmarkTableSettings } from '../api/putTwBenchmarkTableSettings'
import { useNotify } from '../hooks/useNotify'

const EMPTY_COLUMNS_MESSAGE = '表示する列を1つ以上選択してください。'

const SAVE_SUCCESS_MESSAGE = '保存しました。'

/**
 * ColumnKey 型の判定関数
 */
function isColumnKey(column: string): column is ColumnKey {
  return (TABLE_COLUMN_KEYS as readonly string[]).includes(column)
}

/**
 * 表示列設定の orderNo 昇順で並べた ColumnKey 配列を返す。
 */
function normalizeColumnOrder(settings: readonly TableSettingsItem[]): ColumnKey[] {
  return [...settings]
    .sort((a, b) => a.orderNo - b.orderNo)
    .map(item => item.column)
    .filter(isColumnKey)
}

/**
 * X 競合一覧の表示列設定ストア
 */
export const useTwBenchmarkTableSettingsStore = defineStore('twBenchmarkTableSettingsStore', () => {
  const columnOrder = ref<ColumnKey[]>([...TABLE_COLUMN_KEYS])
  const loading = ref(false)

  const previewEnabled = ref(true)

  const visibleColumns = computed<Record<ColumnKey, boolean>>(() => {
    const visibleSet = new Set(columnOrder.value)

    return TABLE_COLUMN_KEYS.reduce(
      (acc, key) => {
        acc[key] = visibleSet.has(key)

        return acc
      },
      {} as Record<ColumnKey, boolean>
    )
  })

  const systemStore = useSystemStore()
  const { handleCustomErrorNotify, handleSuccessNotify } = useNotify()

  const load = async (groupId?: number): Promise<void> => {
    const id = typeof groupId === 'number' ? groupId : systemStore.currentGroupId
    if (typeof id !== 'number') return

    loading.value = true

    try {
      const result = await getTwBenchmarkTableSettings(id)

      if (result.isSuccess && result.data && result.data.settings.length > 0) {
        const normalized = normalizeColumnOrder(result.data.settings)

        if (normalized.length > 0) {
          columnOrder.value = normalized
        }
      }
    } finally {
      loading.value = false
    }
  }

  const save = async (order: ColumnKey[]): Promise<boolean> => {
    const groupId = systemStore.currentGroupId
    if (typeof groupId !== 'number') return false

    const normalized = order.filter(isColumnKey)

    if (normalized.length === 0) {
      handleCustomErrorNotify(EMPTY_COLUMNS_MESSAGE)

      return false
    }

    const settings = normalized.map((column, index) => ({ column, orderNo: index }))

    const result = await putTwBenchmarkTableSettings(groupId, { settings })

    if (result.isSuccess) {
      columnOrder.value = normalized
      handleSuccessNotify(SAVE_SUCCESS_MESSAGE)
    }

    return result.isSuccess
  }

  const setPreviewEnabled = (value: boolean): void => {
    previewEnabled.value = value
  }

  return {
    columnOrder: readonly(columnOrder),
    visibleColumns,
    loading: readonly(loading),
    previewEnabled: readonly(previewEnabled),

    load,
    save,
    setPreviewEnabled
  }
})
